import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import { createSvgIcon } from "@mui/material";

const SlowMotionVideoIcon = createSvgIcon(
  <path d="M19.31,5.6C18.09,5.56 16.88,6.5 16.5,8C16,10 16,10 15,11C13,13 10,14 4,15C3,15.16 2.5,15.5 2,16C4,16 6,16 4.5,17.5L3,19H6L8,17C10,18 11.33,18 13.33,17L14,19H17L16,16C16,16 17,12 18,11C19,10 19,11 20,11C21,11 22,10 22,8.5C22,8 22,7 20.5,6C20.15,5.76 19.74,5.62 19.31,5.6M9,6A6,6 0 0,0 3,12C3,12.6 3.13,13.08 3.23,13.6C9.15,12.62 12.29,11.59 13.93,9.94L14.43,9.44C13.44,7.34 11.32,6 9,6Z" />,
  "Turtle"
);

/**
 * Defines the time in seconds after which the current audio should be reset
 * instead of going to the previous audio.
 * @type {number}
 */
const RESET_BREAKPOINT = 3;

const StyledIconButton = styled(IconButton)`
  padding: 8px !important;
  & svg {
    font-size: 3.125rem !important;
  }
`;

const ControlsWrapper = styled("div")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const AudioElement = styled("audio")`
  display: none;
`;

function PlayerControls({ className, materials, isStudentActive, pages, audioPlaybackRate, toggleAudioPlaybackRate }) {
  const audioElement = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (audioElement.current) {
      audioElement.current.pause();
      audioElement.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrent(0);
  }, [pages]);

  useEffect(() => {
    if (!audioElement || !audioElement.current) return;

    // copy for cleanup function, as audioElement.current might have changed by the time the cleanup function runs.
    const _current = audioElement.current;
    _current.addEventListener("ended", onEnded);
    _current.addEventListener("timeupdate", onTimeupdate);

    return () => {
      _current.removeEventListener("ended", onEnded);
      _current.removeEventListener("timeupdate", onTimeupdate);
    };
  });

  function getMaterial(idx = current) {
    return materials[idx];
  }

  function getAudioSrc(idx = current) {
    const material = getMaterial(idx);
    return material.src || "";
  }

  function onTimeupdate() {
    setCurrentTime(audioElement.current.currentTime);
  }

  function onEnded() {
    const material = getMaterial();
    if (material.autoplayNext && hasNext()) {
      onNextClick();
    } else {
      setIsPlaying(false);
    }
  }

  function onPlayClick() {
    if (!audioElement.current) return;
    if (!isSameSource()) {
      audioElement.current.src = getAudioSrc();
    }

    if (!isPlaying) {
      setIsPlaying(true);
      audioElement.current.play();
    } else {
      setIsPlaying(false);
      audioElement.current.pause();
    }
  }

  function isSameSource() {
    const src = getAudioSrc();
    return audioElement.current.src.endsWith(src.replace(/^\./, ""));
  }

  function hasNext() {
    return current < materials.length - 1;
  }

  function hasPrev() {
    return current > 0;
  }

  function prevDisabled() {
    return !hasPrev() && currentTime === 0;
  }

  function onNextClick() {
    if (hasNext()) {
      audioElement.current.src = getAudioSrc(current + 1);
      setCurrent(current + 1);
      if (isPlaying) audioElement.current.play();
    }
  }

  function shouldReset() {
    if (!audioElement.current) return false;
    return audioElement.current.currentTime > RESET_BREAKPOINT;
  }

  function onPrevClick() {
    if (shouldReset() || !hasPrev()) {
      audioElement.current.currentTime = 0;
    } else if (hasPrev()) {
      audioElement.current.src = getAudioSrc(current - 1);
      setCurrent(current - 1);
      if (isPlaying) audioElement.current.play();
    }
  }

  useEffect(() => {
    if (!audioElement.current) return;

    const playbackRate = audioPlaybackRate ?? 1;
    // due to src changes resetting playbackRate, we set default value to reset to.
    audioElement.current.defaultPlaybackRate = playbackRate;
    audioElement.current.playbackRate = playbackRate;
  }, [audioPlaybackRate]);

  if (!isStudentActive || !materials || materials.length === 0) return null;
  return (
    <ControlsWrapper className={className}>
      <AudioElement ref={audioElement} preload="none" playbackRate={audioPlaybackRate} />
      <Tooltip title="Zurück">
        <StyledIconButton onClick={onPrevClick} disabled={prevDisabled()} size="large" aria-label="Zurück">
          <SkipPreviousIcon />
        </StyledIconButton>
      </Tooltip>
      <Tooltip title={isPlaying ? "Pause" : "Vorlesen"}>
        <StyledIconButton onClick={onPlayClick} size="large" aria-label={isPlaying ? "Pause" : "Vorlesen"}>
          {isPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
        </StyledIconButton>
      </Tooltip>

      <Tooltip title="Vor">
        <StyledIconButton onClick={onNextClick} disabled={!hasNext()} size="large" aria-label="Vor">
          <SkipNextIcon />
        </StyledIconButton>
      </Tooltip>
      <Tooltip title="langsamer lesen">
        <StyledIconButton onClick={() => toggleAudioPlaybackRate()} size="large" aria-label="langsamer lesen">
          <SlowMotionVideoIcon
            style={{
              color: audioPlaybackRate === 1 ? "rgba(0, 0, 0, 0.54)" : "rgb(102, 162, 32)"
            }}
          />
        </StyledIconButton>
      </Tooltip>
    </ControlsWrapper>
  );
}

PlayerControls.propTypes = {
  className: PropTypes.string,
  materials: PropTypes.array.isRequired,
  isStudentActive: PropTypes.bool.isRequired,
  pages: PropTypes.array,
  audioPlaybackRate: PropTypes.number,
  toggleAudioPlaybackRate: PropTypes.func
};

export default PlayerControls;
